import { Lock } from "lucide-react";
import { useAddonCatalog } from "@/hooks/useAddonCatalog";
import { V } from "@/lib/vocab";

// ── Strict badge separation (B4) ──────────────────────────────────────────────
// Pricing/tier and who-can-edit access are DIFFERENT concepts and get DIFFERENT
// visual treatments. Never conflate them.

function priceLabel(a) {
  if (!a || !a.billable) return "";
  if (a.pricing === "tiered") return "Tiered pricing — see Plans & Add-ons";
  return a.price ? `$${a.price}/mo` : "";
}

// PRICING BADGE — derived from the billing add-on catalog (single source of truth).
// Warm sand pill for a paid add-on; soft emerald for anything included in the base plan.
export function PricingBadge({ addonKey, forceIncluded = false, className = "" }) {
  const { byKey, loaded } = useAddonCatalog();
  if (!loaded && !forceIncluded) return null;
  const a = byKey[addonKey];
  const premium = !forceIncluded && !!(a && a.billable);
  const title = premium
    ? `Premium add-on${priceLabel(a) ? ` · ${priceLabel(a)}` : ""} — billing follows the Plans & Add-ons list price`
    : "Included in the base plan at no extra charge";
  return (
    <span
      data-testid={`pricing-badge-${addonKey}`}
      title={title}
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        premium ? "bg-[#E5DED5] text-ink" : "bg-emerald-50 text-emerald-700"
      } ${className}`}
    >
      {premium ? "Premium add-on" : "Included"}
    </span>
  );
}

// ACCESS BADGE — derived from the settings delegation dial. Ink pill with a lock icon.
// Distinct from pricing so "who can edit" is never mistaken for "what it costs".
// Seat titles come from the industry pack ("Community Managers"), never internal role keys.
const accessLabel = (level) => ({ redline: "Redline only", company: `${V.role("company_admin")}s only`, managers: `${V.role("hoa_manager")}s and above`, board: `${V.role("hoa_manager")}s + ${V.grp()}` })[level];
export function AccessBadge({ level = "redline", className = "" }) {
  const label = accessLabel(level) || accessLabel("redline");
  const restricted = level === "redline";
  return (
    <span
      data-testid={`access-badge-${level}`}
      title={`Editable by ${label}`}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${
        restricted ? "bg-ink text-white border-ink" : "bg-white text-ink border-ink/30"
      } ${className}`}
    >
      <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />
      {label}
    </span>
  );
}
